import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// PROTOTYPE — a localStorage-backed mock of the split-payment schedule. No
// backend; the real split-plan flow lives in FinanceTab/AcceptedSplit. This
// only powers the "Create Split Payment Schedule" wireframe demo.

export type MockScheduleStatus = 'draft' | 'under-review' | 'approved';

export type MockInstallment = {
  id: string;
  dueDate: string | null;
  // SR-specified amount the client pays for this installment (paying currency).
  dueAmount: number | null;
  // Mock issuance: installment #1 issues on approval; others on "Issue now".
  issued: boolean;
  // Mock payment marking (no backend record for installment invoices).
  paid?: boolean;
};

export type MockSchedule = {
  status: MockScheduleStatus;
  installments: MockInstallment[];
  // offerItemId (as string) -> installment id. Absent key = not yet distributed.
  assignment: Record<string, string>;
};
type Schedule = MockSchedule;

type State = {
  schedules: Record<number, Schedule>;
  seed: (offerId: number) => void;
  reset: (offerId: number) => void;
  addInstallment: (offerId: number) => void;
  removeInstallment: (offerId: number, instId: string) => void;
  assign: (offerId: number, serviceKey: string, instId: string) => void;
  unassign: (offerId: number, serviceKey: string) => void;
  setDueDate: (offerId: number, instId: string, date: string | null) => void;
  setDueAmount: (
    offerId: number,
    instId: string,
    amount: number | null,
  ) => void;
  submit: (offerId: number) => void;
  approve: (offerId: number) => void;
  returnToDraft: (offerId: number) => void;
  issueInstallment: (offerId: number, instId: string) => void;
  markInstallmentPaid: (offerId: number, instId: string) => void;
};

const newId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `i${Date.now()}${Math.round(Math.random() * 1e6)}`;

// Mutate a schedule. `editableOnly` no-ops the change unless the schedule is in
// draft — enforces "under review cannot be modified".
const patch = (
  state: State,
  offerId: number,
  fn: (s: Schedule) => Schedule,
  editableOnly = true,
): State => {
  const sch = state.schedules[offerId];
  if (!sch) return state;
  const current = sch.status ?? 'draft';
  if (editableOnly && current !== 'draft') return state;
  return { ...state, schedules: { ...state.schedules, [offerId]: fn(sch) } };
};

export const useMockSplitSchedule = create<State>()(
  persist(
    (set, get) => ({
      schedules: {},

      seed: (offerId) => {
        if (get().schedules[offerId]) return;
        // Services start unplaced — the SR distributes them into installments.
        set((s) => ({
          schedules: {
            ...s.schedules,
            [offerId]: {
              status: 'draft',
              installments: [
                { id: newId(), dueDate: null, dueAmount: null, issued: false },
              ],
              assignment: {},
            },
          },
        }));
      },

      reset: (offerId) =>
        set((s) => {
          const next = { ...s.schedules };
          delete next[offerId];
          return { schedules: next };
        }),

      addInstallment: (offerId) =>
        set((s) =>
          patch(s, offerId, (sch) => ({
            ...sch,
            installments: [
              ...sch.installments,
              { id: newId(), dueDate: null, dueAmount: null, issued: false },
            ],
          })),
        ),

      removeInstallment: (offerId, instId) =>
        set((s) =>
          patch(s, offerId, (sch) => {
            if (sch.installments.length <= 1) return sch;
            const installments = sch.installments.filter(
              (i) => i.id !== instId,
            );
            // Its services return to the undistributed pool.
            const assignment = Object.fromEntries(
              Object.entries(sch.assignment).filter(([, v]) => v !== instId),
            );
            return { ...sch, installments, assignment };
          }),
        ),

      assign: (offerId, serviceKey, instId) =>
        set((s) =>
          patch(s, offerId, (sch) => ({
            ...sch,
            assignment: { ...sch.assignment, [serviceKey]: instId },
          })),
        ),

      unassign: (offerId, serviceKey) =>
        set((s) =>
          patch(s, offerId, (sch) => {
            const assignment = { ...sch.assignment };
            delete assignment[serviceKey];
            return { ...sch, assignment };
          }),
        ),

      setDueDate: (offerId, instId, date) =>
        set((s) =>
          patch(s, offerId, (sch) => ({
            ...sch,
            installments: sch.installments.map((i) =>
              i.id === instId ? { ...i, dueDate: date } : i,
            ),
          })),
        ),

      setDueAmount: (offerId, instId, amount) =>
        set((s) =>
          patch(s, offerId, (sch) => ({
            ...sch,
            installments: sch.installments.map((i) =>
              i.id === instId ? { ...i, dueAmount: amount } : i,
            ),
          })),
        ),

      submit: (offerId) =>
        set((s) =>
          patch(s, offerId, (sch) => ({ ...sch, status: 'under-review' })),
        ),

      approve: (offerId) =>
        set((s) =>
          patch(
            s,
            offerId,
            (sch) =>
              (sch.status ?? 'draft') === 'under-review'
                ? {
                    ...sch,
                    status: 'approved',
                    // Installment #1 issues immediately, replacing the original.
                    installments: sch.installments.map((i, idx) =>
                      idx === 0 ? { ...i, issued: true } : i,
                    ),
                  }
                : sch,
            false,
          ),
        ),

      returnToDraft: (offerId) =>
        set((s) =>
          patch(
            s,
            offerId,
            (sch) => ({
              ...sch,
              status: 'draft',
              installments: sch.installments.map((i) => ({
                ...i,
                issued: false,
                paid: false,
              })),
            }),
            false,
          ),
        ),

      issueInstallment: (offerId, instId) =>
        set((s) =>
          patch(
            s,
            offerId,
            (sch) =>
              (sch.status ?? 'draft') === 'approved'
                ? {
                    ...sch,
                    installments: sch.installments.map((i) =>
                      i.id === instId ? { ...i, issued: true } : i,
                    ),
                  }
                : sch,
            false,
          ),
        ),

      markInstallmentPaid: (offerId, instId) =>
        set((s) =>
          patch(
            s,
            offerId,
            (sch) =>
              (sch.status ?? 'draft') === 'approved'
                ? {
                    ...sch,
                    installments: sch.installments.map((i) =>
                      i.id === instId && i.issued ? { ...i, paid: true } : i,
                    ),
                  }
                : sch,
            false,
          ),
        ),
    }),
    {
      name: 'mock-split-schedule',
      // v3: added per-installment `issued`. Earlier shapes (offer_item keys, no
      // issued flag) are dropped so schedules reseed cleanly.
      version: 3,
      migrate: () => ({ schedules: {} }),
    },
  ),
);
